import React, { useState } from 'react';
import { Bookmark, Check, Pencil, Trash2, Plus, X } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './ui/tooltip';

// Compact bar of saved dashboard views.
// - Click a chip to apply that view.
// - Active view highlighted (matches current filter+snapshot+toggle state exactly).
// - "Save current view" opens a small inline form to enter name + optional description.
// - Hover any saved view chip to see the description.
// - Edit mode lets user change both name and description.
export default function SavedViewsBar({
  views = [],
  currentState,  // { selected, subSel, geoSel, stratSel, showPct }
  onApply,
  onSave,        // (name, description) => void
  onUpdate,      // (id, { name, description }) => void
  onDelete,
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');

  const sameArr = (a = [], b = []) => a.length === b.length && a.every((x) => b.includes(x));

  const matches = (v) =>
    sameArr(v.selected, currentState.selected) &&
    sameArr(v.subSel, currentState.subSel) &&
    sameArr(v.geoSel, currentState.geoSel) &&
    sameArr(v.stratSel, currentState.stratSel) &&
    !!v.showPct === !!currentState.showPct;

  const activeId = views.find(matches)?.id || null;

  const handleSave = () => {
    const name = newName.trim();
    if (!name) return;
    onSave(name, newDesc.trim());
    setNewName('');
    setNewDesc('');
    setAddOpen(false);
  };

  const startEdit = (v) => {
    setEditingId(v.id);
    setEditName(v.name);
    setEditDesc(v.description || '');
  };
  const commitEdit = () => {
    const name = editName.trim();
    if (!name) { setEditingId(null); return; }
    onUpdate(editingId, { name, description: editDesc.trim() });
    setEditingId(null);
  };
  const cancelEdit = () => setEditingId(null);

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex items-start gap-2 flex-wrap mb-3" data-testid="saved-views-bar">
        <div className="flex items-center gap-1.5 text-[#575757] pt-1">
          <Bookmark size={13} />
          <span className="overline">Saved views</span>
        </div>
        {views.length === 0 && !addOpen && (
          <span className="text-[11px] font-mono text-[#AAA] pt-1">— none yet · save your current filters as a preset</span>
        )}

        {views.map((v) => {
          const isActive = activeId === v.id;
          const isEditing = editingId === v.id;
          if (isEditing) {
            return (
              <div
                key={v.id}
                className="flex flex-col gap-1.5 border border-[hsl(var(--primary))] bg-white p-2 rounded text-[12px] min-w-[260px]"
                data-testid={`saved-view-edit-${v.id}`}
              >
                <input
                  autoFocus
                  className="border border-border px-2 py-1 outline-none focus:border-[hsl(var(--primary))]"
                  placeholder="View name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  data-testid={`saved-view-edit-name-${v.id}`}
                />
                <textarea
                  rows={2}
                  className="border border-border px-2 py-1 outline-none focus:border-[hsl(var(--primary))] resize-none text-[11px]"
                  placeholder="Description (optional)"
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  data-testid={`saved-view-edit-desc-${v.id}`}
                />
                <div className="flex justify-end gap-1.5">
                  <button
                    className="btn-secondary !px-2.5 !py-1 text-[11px]"
                    onClick={cancelEdit}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-primary !px-2.5 !py-1 text-[11px]"
                    onClick={commitEdit}
                    data-testid={`saved-view-edit-commit-${v.id}`}
                  >
                    Save
                  </button>
                </div>
              </div>
            );
          }
          const tooltipBody = v.description
            ? (
              <div className="max-w-[240px]">
                <div className="font-heading font-semibold mb-1">{v.name}</div>
                <div className="text-[11px] opacity-90 whitespace-pre-wrap">{v.description}</div>
              </div>
            )
            : (<span>{v.name}</span>);
          return (
            <Tooltip key={v.id}>
              <TooltipTrigger asChild>
                <div
                  className={`group flex items-center gap-1 border pl-2.5 pr-1 py-1 rounded-full text-[12px] transition-colors ${
                    isActive
                      ? 'bg-[hsl(var(--primary))] border-[hsl(var(--primary))] text-white'
                      : 'bg-white border-border hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]'
                  }`}
                  data-testid={`saved-view-${v.id}`}
                >
                  <button
                    onClick={() => onApply(v)}
                    className="font-heading font-semibold"
                    data-testid={`saved-view-apply-${v.id}`}
                  >
                    {v.name}
                  </button>
                  {v.description && (
                    <span className={`text-[9px] font-mono ${isActive ? 'opacity-80' : 'text-[#AAA]'}`}>·</span>
                  )}
                  <button
                    className="p-1 opacity-0 group-hover:opacity-70 hover:opacity-100"
                    onClick={() => startEdit(v)}
                    title="Edit name & description"
                    data-testid={`saved-view-rename-${v.id}`}
                  >
                    <Pencil size={10} />
                  </button>
                  <button
                    className="p-1 opacity-0 group-hover:opacity-70 hover:opacity-100"
                    onClick={() => { if (window.confirm(`Delete view "${v.name}"?`)) onDelete(v.id); }}
                    title="Delete"
                    data-testid={`saved-view-delete-${v.id}`}
                  >
                    <Trash2 size={10} />
                  </button>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="bg-[#2B2B2B] text-white">
                {tooltipBody}
              </TooltipContent>
            </Tooltip>
          );
        })}

        {addOpen ? (
          <div className="flex flex-col gap-1.5 border border-[hsl(var(--primary))] bg-white p-2 rounded text-[12px] min-w-[260px]">
            <input
              autoFocus
              className="border border-border px-2 py-1 outline-none focus:border-[hsl(var(--primary))]"
              placeholder="View name…"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) handleSave();
                if (e.key === 'Escape') { setAddOpen(false); setNewName(''); setNewDesc(''); }
              }}
              data-testid="saved-view-new-name"
            />
            <textarea
              rows={2}
              className="border border-border px-2 py-1 outline-none focus:border-[hsl(var(--primary))] resize-none text-[11px]"
              placeholder="Description (optional)…"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              data-testid="saved-view-new-desc"
            />
            <div className="flex justify-end gap-1.5">
              <button
                className="btn-secondary !px-2.5 !py-1 text-[11px]"
                onClick={() => { setAddOpen(false); setNewName(''); setNewDesc(''); }}
              >
                <X size={11} className="inline mr-1" /> Cancel
              </button>
              <button
                className="btn-primary !px-2.5 !py-1 text-[11px] disabled:opacity-50"
                onClick={handleSave}
                disabled={!newName.trim()}
                data-testid="saved-view-confirm"
              >
                <Check size={11} className="inline mr-1" /> Save view
              </button>
            </div>
          </div>
        ) : (
          <button
            className="flex items-center gap-1 border border-dashed border-[#B0B0A8] text-[#575757] pl-2.5 pr-3 py-1 rounded-full text-[12px] font-heading font-semibold hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]"
            onClick={() => setAddOpen(true)}
            data-testid="saved-view-add"
          >
            <Plus size={12} /> Save current view
          </button>
        )}
      </div>
    </TooltipProvider>
  );
}
